import React, { useState, useEffect, useCallback } from 'react';

// --- OYUN AYARLARI ---
const BOARD_SIZE = 8;
const ANIMATION_MS = 300;

// Şeker Tipleri (Görsel sınıflar için)
const CANDY_TYPES = [
  'red-bean',    // Kırmızı Fasulye
  'orange-drop', // Turuncu Damla
  'yellow-cube', // Sarı Küp
  'green-poly',  // Yeşil Çokgen
  'blue-sphere', // Mavi Küre
  'purple-star'  // Mor Yıldız
];

// --- SES MOTORU ---
const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
const playSound = (type) => {
  if (audioCtx.state === 'suspended') audioCtx.resume();
  const osc = audioCtx.createOscillator();
  const gainNode = audioCtx.createGain();
  osc.connect(gainNode);
  gainNode.connect(audioCtx.destination);
  const now = audioCtx.currentTime;

  if (type === 'pop') {
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.exponentialRampToValueAtTime(100, now + 0.15);
    gainNode.gain.setValueAtTime(0.2, now);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
  } else if (type === 'swap') {
    osc.frequency.setValueAtTime(200, now);
    osc.frequency.linearRampToValueAtTime(400, now + 0.1);
    gainNode.gain.setValueAtTime(0.1, now);
    gainNode.gain.linearRampToValueAtTime(0, now + 0.1);
  }
  osc.start(now);
  osc.stop(now + 0.2);
};

export default function CandyCrush() {
  const [board, setBoard] = useState([]);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ , setAnimating] = useState(null); // 'invalid' | null
  
  // Touch için
  const [touchStart, setTouchStart] = useState(null);

  // --- OYUN MANTIĞI ---
  const createBoard = useCallback(() => {
    const newBoard = [];
    for (let r = 0; r < BOARD_SIZE; r++) {
      const row = [];
      for (let c = 0; c < BOARD_SIZE; c++) {
        row.push({
          id: Math.random().toString(36).substr(2, 9),
          type: Math.floor(Math.random() * CANDY_TYPES.length),
        });
      }
      newBoard.push(row);
    }
    if (findMatches(newBoard).length > 0) return createBoard();
    return newBoard;
  }, []);

  const findMatches = (currentBoard) => {
    const matches = [];
    // Yatay
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE - 2; c++) {
        const t1 = currentBoard[r][c]?.type;
        const t2 = currentBoard[r][c+1]?.type;
        const t3 = currentBoard[r][c+2]?.type;
        if (t1 !== undefined && t1 === t2 && t2 === t3) {
          matches.push({r, c}, {r, c: c+1}, {r, c: c+2});
        }
      }
    }
    // Dikey
    for (let c = 0; c < BOARD_SIZE; c++) {
      for (let r = 0; r < BOARD_SIZE - 2; r++) {
        const t1 = currentBoard[r][c]?.type;
        const t2 = currentBoard[r+1][c]?.type;
        const t3 = currentBoard[r+2][c]?.type;
        if (t1 !== undefined && t1 === t2 && t2 === t3) {
          matches.push({r, c}, {r: r+1, c}, {r: r+2, c});
        }
      }
    }
    return [...new Set(matches.map(m => JSON.stringify(m)))].map(s => JSON.parse(s));
  };

  const processBoard = async (currentBoard) => {
    let activeBoard = JSON.parse(JSON.stringify(currentBoard));
    let loop = true;
    
    while (loop) {
      const matches = findMatches(activeBoard);
      if (matches.length === 0) {
        loop = false;
        break;
      }

      // Patlat
      playSound('pop');
      matches.forEach(({r, c}) => {
         activeBoard[r][c] = null;
      });
      setScore(prev => prev + matches.length * 10);
      setBoard([...activeBoard]);
      
      await new Promise(r => setTimeout(r, ANIMATION_MS));

      // Düşür
      for (let c = 0; c < BOARD_SIZE; c++) {
        let emptyCount = 0;
        for (let r = BOARD_SIZE - 1; r >= 0; r--) {
          if (activeBoard[r][c] === null) {
            emptyCount++;
          } else if (emptyCount > 0) {
            activeBoard[r + emptyCount][c] = activeBoard[r][c];
            activeBoard[r][c] = null;
          }
        }
        // Yeni doldur
        for (let r = 0; r < emptyCount; r++) {
          activeBoard[r][c] = {
            id: Math.random().toString(36).substr(2, 9),
            type: Math.floor(Math.random() * CANDY_TYPES.length)
          };
        }
      }
      setBoard([...activeBoard]);
      await new Promise(r => setTimeout(r, ANIMATION_MS));
    }
    setIsProcessing(false);
  };

  const handleInteraction = async (r, c) => {
    if (isProcessing) return;

    if (!selected) {
      setSelected({r, c});
      playSound('swap');
      return;
    }

    if (selected.r === r && selected.c === c) {
      setSelected(null);
      return;
    }

    const isAdjacent = Math.abs(selected.r - r) + Math.abs(selected.c - c) === 1;

    if (isAdjacent) {
      setIsProcessing(true);
      playSound('swap');
      
      const tempBoard = JSON.parse(JSON.stringify(board));
      const temp = tempBoard[r][c];
      tempBoard[r][c] = tempBoard[selected.r][selected.c];
      tempBoard[selected.r][selected.c] = temp;
      
      setBoard(tempBoard);
      setSelected(null);

      await new Promise(res => setTimeout(res, ANIMATION_MS));

      if (findMatches(tempBoard).length > 0) {
        await processBoard(tempBoard);
      } else {
        // Geçersiz hamle
        playSound('swap'); // Geri dönüş sesi
        setAnimating({type: 'invalid', r1: selected.r, c1: selected.c, r2: r, c2: c});
        setBoard(board); // Eskiye dön
        setTimeout(() => {
            setAnimating(null);
            setIsProcessing(false);
        }, ANIMATION_MS);
      }
    } else {
      setSelected({r, c});
      playSound('swap');
    }
  };

  // Touch Handlers
  const onTouchStart = (e, r, c) => {
      const t = e.touches[0];
      setTouchStart({x: t.clientX, y: t.clientY, r, c});
  };

  const onTouchEnd = (e) => {
      if(!touchStart || isProcessing) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStart.x;
      const dy = t.clientY - touchStart.y;
      
      if(Math.abs(dx) < 20 && Math.abs(dy) < 20) {
          handleInteraction(touchStart.r, touchStart.c);
      } else {
          let tr = touchStart.r;
          let tc = touchStart.c;
          if(Math.abs(dx) > Math.abs(dy)) {
              tc += dx > 0 ? 1 : -1;
          } else {
              tr += dy > 0 ? 1 : -1;
          }
          if(tr >= 0 && tr < BOARD_SIZE && tc >= 0 && tc < BOARD_SIZE) {
              if(!selected) {
                  setSelected({r: touchStart.r, c: touchStart.c});
                  attemptSwapLogic(touchStart.r, touchStart.c, tr, tc);
              } else {
                  handleInteraction(tr, tc);
              }
          }
      }
      setTouchStart(null);
  };

  const attemptSwapLogic = async (r1, c1, r2, c2) => {
      if (isProcessing) return;
      setIsProcessing(true);
      playSound('swap');
      
      const tempBoard = JSON.parse(JSON.stringify(board));
      const temp = tempBoard[r1][c1];
      tempBoard[r1][c1] = tempBoard[r2][c2];
      tempBoard[r2][c2] = temp;
      
      setBoard(tempBoard);
      setSelected(null);

      await new Promise(res => setTimeout(res, ANIMATION_MS));

      if (findMatches(tempBoard).length > 0) {
        await processBoard(tempBoard);
      } else {
        setBoard(board);
        setIsProcessing(false);
      }
  };

  useEffect(() => {
    setBoard(createBoard());
  }, [createBoard]);

  return (
    <div className="game-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&display=swap');

        :root {
          --bg-gradient: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
          --board-bg: rgba(255, 255, 255, 0.6);
          --board-shadow: 0 10px 30px rgba(0,0,0,0.1), inset 0 0 0 2px rgba(255,255,255,0.8);
        }

        body { 
          margin: 0; 
          padding: 0;
          overflow: hidden; 
          background: #FFDEE9; 
          background-image: linear-gradient(0deg, #FFDEE9 0%, #B5FFFC 100%); 
          font-family: 'Fredoka One', cursive; 
          touch-action: none; /* Mobilde scroll ve zoom'u engelle */
          width: 100vw;
          height: 100dvh; /* Dinamik viewport yüksekliği */
        }

        .game-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          /* justify-content: center ile her şeyi tekrar bir araya topluyoruz */
          justify-content: center;
          /* Elemanlar arasında dengeli boşluk */
          gap: clamp(15px, 3vh, 30px);
          height: 100%;
          width: 100%;
          /* Alt kısımdan büyük bir padding vererek tüm içeriği YUKARI itiyoruz */
          padding-bottom: 12vh;
          box-sizing: border-box;
        }

        /* --- BAŞLIK & SKOR --- */
        .header {
          flex: 0 0 auto;
          text-align: center;
          z-index: 2;
          width: 100%;
          margin-bottom: 5px;
        }

        .title {
          font-size: clamp(2.5rem, 6vh, 3.5rem);
          color: #fff;
          text-shadow: 2px 2px 0px #FF80AB, 4px 4px 0px rgba(0,0,0,0.1);
          margin: 0;
          letter-spacing: 2px;
          line-height: 1.1;
        }

        .score-box {
          background: #fff;
          padding: 5px 25px;
          border-radius: 50px;
          display: inline-block;
          margin-top: 5px;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
          color: #FF80AB;
          font-size: clamp(1.2rem, 3vh, 1.5rem);
          border: 3px solid #fff;
        }

        /* --- OYUN TAHTASI (Responsive) --- */
        .board-wrapper {
          flex: 0 0 auto;
          background: var(--board-bg);
          padding: 10px;
          border-radius: 20px;
          box-shadow: var(--board-shadow);
          backdrop-filter: blur(10px);
          
          /* Yüksekliği 50vh yaparak eski ideal boyuta getirdik */
          width: min(90vw, 50vh, 450px);
          aspect-ratio: 1 / 1;
          
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .grid {
          display: grid;
          grid-template-columns: repeat(${BOARD_SIZE}, 1fr);
          grid-template-rows: repeat(${BOARD_SIZE}, 1fr);
          gap: 1.5%;
          width: 100%;
          height: 100%;
        }

        .cell {
          width: 100%;
          height: 100%;
          position: relative;
          cursor: pointer;
          border-radius: 15%;
          background: rgba(255,255,255,0.3);
          transition: transform 0.2s, background 0.2s;
        }

        .cell.selected {
          background: rgba(255,255,255,0.8);
          box-shadow: 0 0 10px #FF80AB;
          transform: scale(1.05);
          z-index: 10;
        }

        /* --- ŞEKERLER --- */
        .candy {
          width: 80%;
          height: 80%;
          margin: 10%;
          border-radius: 50%;
          position: relative;
          box-shadow: inset -2px -2px 4px rgba(0,0,0,0.2), 2px 2px 4px rgba(0,0,0,0.2);
          transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .candy::after {
          content: '';
          position: absolute;
          top: 15%; left: 15%; width: 30%; height: 20%;
          border-radius: 50%;
          background: linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,0.1));
          filter: blur(1px);
        }

        .candy-enter { animation: popIn 0.4s ease-out; }

        .red-bean { background: radial-gradient(circle at 30% 30%, #ff758c, #ff7eb3); border-radius: 40% 40% 45% 45%; }
        .orange-drop { background: radial-gradient(circle at 30% 30%, #ffd46d, #ffae52); border-radius: 50% 50% 50% 0; transform: rotate(-45deg); }
        .yellow-cube { background: radial-gradient(circle at 30% 30%, #fff6b7, #f6e27a); border-radius: 20%; width: 70%; height: 70%; margin: 15%; }
        .green-poly { background: radial-gradient(circle at 30% 30%, #9be15d, #00e3ae); border-radius: 50%; clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%); }
        .blue-sphere { background: radial-gradient(circle at 30% 30%, #6cdbeb, #53a0fd); border-radius: 50%; }
        .purple-star { background: radial-gradient(circle at 30% 30%, #e0c3fc, #8ec5fc); clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%); border-radius: 0; }
        
        @keyframes popIn {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }

        .btn-reset {
          flex: 0 0 auto;
          background: #fff;
          border: none;
          padding: 12px 30px;
          border-radius: 50px;
          color: #53a0fd;
          font-family: 'Fredoka One', cursive;
          font-size: clamp(1rem, 2.5vh, 1.2rem);
          cursor: pointer;
          box-shadow: 0 10px 20px rgba(83, 160, 253, 0.3);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .btn-reset:active { transform: scale(0.95); box-shadow: 0 5px 10px rgba(83, 160, 253, 0.2); }

      `}</style>

      <div className="header">
        <h1 className="title">Candy Pop</h1>
        <div className="score-box">
          {score}
        </div>
      </div>

      <div className="board-wrapper">
        <div className="grid">
          {board.map((row, r) => (
            row.map((cell, c) => {
              const isSelected = selected?.r === r && selected?.c === c;
              
              return (
                <div 
                  key={cell ? cell.id : `empty-${r}-${c}`}
                  className={`cell ${isSelected ? 'selected' : ''}`}
                  onMouseDown={() => handleInteraction(r, c)}
                  onTouchStart={(e) => onTouchStart(e, r, c)}
                  onTouchEnd={onTouchEnd}
                >
                  {cell && (
                    <div className={`candy ${CANDY_TYPES[cell.type]} candy-enter`}></div>
                  )}
                </div>
              );
            })
          ))}
        </div>
      </div>

      <button className="btn-reset" onClick={() => { setScore(0); setBoard(createBoard()); }}>
        Tekrar Oyna
      </button>

    </div>
  );
}